import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Loader2, Search, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import SiteLayout from "@/components/site/SiteLayout";
import { SectionTag } from "@/components/site/Reveal";
import ParticleField from "@/components/site/ParticleField";
import { fetchBlog, toAbsolute } from "@/lib/api";

export default function Blog() {
  const { t, dir, lang } = useLang();
  const [posts, setPosts] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const Arrow = dir === "rtl" ? ArrowLeft : ArrowRight;

  useEffect(() => {
    fetchBlog(lang).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, [lang]);

  const categories = useMemo(() => Array.from(new Set((posts || []).map((p) => p.category).filter(Boolean))), [posts]);

  const filtered = useMemo(() => {
    if (!posts) return null;
    const s = q.trim().toLowerCase();
    return posts.filter((p) => {
      if (cat && p.category !== cat) return false;
      if (!s) return true;
      const hay = `${p.title} ${p.excerpt || ""} ${p.category || ""} ${p.author || ""}`.toLowerCase();
      return hay.includes(s);
    });
  }, [posts, q, cat]);

  return (
    <SiteLayout>
      <section className="relative overflow-hidden pt-16 pb-10" data-testid="blog-page">
        <ParticleField className="opacity-40" density={30} />
        <div className="absolute inset-0 radial-fade" />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <SectionTag>{t.blog.tag}</SectionTag>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-5 mb-4">{t.blog.title}</h1>
          <p className="text-slate-400 text-lg max-w-2xl">{t.blog.subtitle}</p>
        </div>
      </section>

      <section className="section-pad pt-6">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {/* Search + Filters */}
          <div className="mb-8 flex flex-col md:flex-row gap-3" data-testid="blog-filters">
            <div className="relative flex-1">
              <Search size={16} className="absolute top-1/2 -translate-y-1/2 start-4 text-slate-500 pointer-events-none" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t.filters.searchBlog}
                className="w-full rounded-xl bg-[#0d1836] border border-white/10 ps-11 pe-10 py-3 text-sm focus:outline-none focus:border-[color:var(--primary)]"
                data-testid="blog-search-input"
              />
              {q && (
                <button onClick={() => setQ("")} className="absolute top-1/2 -translate-y-1/2 end-3 h-6 w-6 rounded-full grid place-items-center hover:bg-white/10 text-slate-400" aria-label="clear">
                  <X size={14} />
                </button>
              )}
            </div>
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setCat("")}
                  className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${!cat ? "text-[#04120a]" : "border-white/15 hover:border-[color:var(--primary)] text-slate-300"}`}
                  style={!cat ? { background: "var(--primary)", borderColor: "var(--primary)" } : {}}
                  data-testid="blog-filter-all"
                >
                  {t.filters.all}
                </button>
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCat(cat === c ? "" : c)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${cat === c ? "text-[#04120a]" : "border-white/15 hover:border-[color:var(--primary)] text-slate-300"}`}
                    style={cat === c ? { background: "var(--primary)", borderColor: "var(--primary)" } : {}}
                    data-testid={`blog-filter-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>
          {filtered === null ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-slate-500 py-20" data-testid="blog-empty">{q || cat ? t.filters.noResults : t.blog.empty}</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                >
                  <Link to={`/blog/${encodeURIComponent(p.slug)}`} className="group block h-full card-surface rounded-3xl overflow-hidden" data-testid={`blog-card-${i}`}>
                    <div className="relative aspect-video overflow-hidden">
                      <img src={toAbsolute(p.cover_image)} alt={p.title} loading="lazy" className="h-full w-full object-cover scale-105 group-hover:scale-110 transition-transform duration-700" />
                      {p.category && <span className="absolute top-4 start-4 rounded-full px-3 py-1 text-xs font-bold text-[#04120a]" style={{ background: "var(--primary)" }}>{p.category}</span>}
                    </div>
                    <div className="p-6" dir="auto">
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                        <Clock size={13} /> {p.read_time}
                      </div>
                      <h3 className="font-bold text-xl mb-2 leading-snug group-hover:text-primary transition-colors line-clamp-2">{p.title}</h3>
                      <p className="text-slate-400 text-sm leading-relaxed line-clamp-3">{p.excerpt}</p>
                      <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-primary">
                        {t.blog.readMore} <Arrow size={16} className="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
